import { usePosthog } from "~/composables/usePosthog";
import { getCoverageShortfall, isEstimateAboveCover, type CoverageSnapshot } from "~/utils/credit-insurer-coverage";

export type CoverageSource = "company_tab" | "order" | "contract";

// Tracking must never get in the way of a cover lookup, a request or a creation.
export const useCoverageTracking = () => {
  const posthog = usePosthog();

  const track = (...args: Parameters<typeof posthog.trackEvent>) => {
    posthog.trackEvent(...args).catch(() => undefined);
  };

  const trackTabViewed = (companyId: string, coverageState: string) =>
    track("coverage_tab_viewed", { company_id: companyId, coverage_state: coverageState });

  const trackRequestModalOpened = (companyId: string, source: CoverageSource) =>
    track("coverage_request_modal_opened", { company_id: companyId, source });

  const trackRequestSubmitted = (companyId: string, source: CoverageSource, requestedAmount: number) =>
    track("coverage_request_submitted", { company_id: companyId, source, requested_amount: requestedAmount });

  const trackRequestFailed = (companyId: string, source: CoverageSource, errorMessage: string) =>
    track("coverage_request_failed", { company_id: companyId, source, error_message: errorMessage });

  const trackBannerViewed = (companyId: string, screen: "order" | "contract", snapshot: CoverageSnapshot) =>
    track("coverage_banner_viewed", {
      company_id: companyId,
      screen,
      coverage_state: snapshot.state.kind,
      is_estimate_above_coverage: isEstimateAboveCover(snapshot),
    });

  const trackBannerCtaClicked = (companyId: string, screen: "order" | "contract", coverageState: string) =>
    track("coverage_banner_cta_clicked", { company_id: companyId, screen, coverage_state: coverageState });

  const trackCreatedWithoutCoverage = (
    companyId: string,
    screen: "order" | "contract",
    snapshot: CoverageSnapshot | undefined
  ) => {
    if (!snapshot) return;
    const shortfall = getCoverageShortfall(snapshot);
    if (!shortfall) return;
    track(screen === "order" ? "order_created_without_coverage" : "contract_created_without_coverage", {
      company_id: companyId,
      coverage_state: snapshot.state.kind,
      estimated_amount: snapshot.estimatedAmount,
      coverage_gap_amount: shortfall.gapAmount,
    });
  };

  return {
    trackTabViewed,
    trackRequestModalOpened,
    trackRequestSubmitted,
    trackRequestFailed,
    trackBannerViewed,
    trackBannerCtaClicked,
    trackCreatedWithoutCoverage,
  };
};
